import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { MainLayout } from './MainLayout';
import { useCompany } from '../contexts/CompanyContext';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase_client';
import { useParams } from 'react-router-dom'
import { TeamMemberModal } from './TeamMemberModal';
import Groups from './Groups';
import '../styles/page-base.css';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role_id: number;
  avatar?: string;
  isCurrentUser?: boolean;
  phone?: string;
  group_ids?: string[];
  group_names?: string[];
  invitationStatus?: 'pending' | 'accepted' | 'active'; 
  invitationId?: string;
}

interface Group {
  id: string;
  name: string;
  description?: string;
  member_count?: number;
}

export default function Team() {
  const [searchTerm, setSearchTerm] = useState('');
  const { companyId } = useParams<{ companyId: string }>();
  const { selectedCompany, setSelectedCompany, userCompanies } = useCompany();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [currentUserRoleId, setCurrentUserRoleId] = useState<number>(0);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [showRoleDropdown, setShowRoleDropdown] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [roleName, setRoleName] = useState<{ [key: number]: string }>({});
  const { user, isUserAdmin, permissions, refresh } = useSession();
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [editingGroupDescription, setEditingGroupDescription] = useState('');
  const [showGroupAssignmentModal, setShowGroupAssignmentModal] = useState<string | null>(null);
  const [tempSelectedGroups, setTempSelectedGroups] = useState<string[]>([]);
  const [currentUserGroupIds, setCurrentUserGroupIds] = useState<string[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const refreshGroupsRef = useRef<(() => Promise<void>) | null>(null);
  const [showInvitationMenu, setShowInvitationMenu] = useState<string | null>(null);
  const [showResendInviteConfirm, setShowResendInviteConfirm] = useState<{id: string, email: string, roleId: number} | null>(null);
  const [showCancelInviteConfirm, setShowCancelInviteConfirm] = useState<{id: string, email: string} | null>(null);

  const getAvailableRoles = (currentUserRoleId: number): number[] => {
    switch (currentUserRoleId) {
      case 1:
        return [1, 2, 3, 4, 5];
      case 2:
        return [2, 3, 4, 5];
      case 3:
        return [3, 4, 5];
      case 4:
        return [4, 5];
      default:
        return [5];
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Close role dropdown
      if (!target.closest('.role-dropdown-container')) {
        setShowRoleDropdown(null);
      }
      
      // Close invitation menu
      if (!target.closest('.invitation-menu-container')) {
        setShowInvitationMenu(null);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchRoleNames = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('id, role_name')
        .order('id');
      
      if (error) {
        console.error('Error fetching role names:', error);
        return;
      }
      
      const roleMap: {[key: number]: string} = {};
      data?.forEach(role => {
        roleMap[role.id] = role.role_name.toLowerCase();
      });
      
      setRoleName(roleMap);
    } catch (error) {
      console.error('Error fetching role names:', error);
    }
  }, []);

  const handleRoleChange = async (memberId: string, newRoleId: number) => {
    try {
      // Prevent changing FROM owner role
      const member = teamMembers.find(m => m.id === memberId);
      if (member?.role_id === 1) {
        setErrorMsg('Owner role cannot be changed. There must always be one owner in the organization.');
        setTimeout(() => setErrorMsg(null), 3000);
        setShowRoleDropdown(null);
        return;
      }

      // Prevent changing TO owner role if there's already an owner
      if (newRoleId === 1) {
        const existingOwner = teamMembers.find(m => m.role_id === 1 && m.id !== memberId);
        if (existingOwner) {
          setErrorMsg('Cannot assign owner role. There is already an owner in this organization. Only one owner is allowed.');
          setTimeout(() => setErrorMsg(null), 3000);
          setShowRoleDropdown(null);
          return;
        }
      }

      const { error } = await supabase
        .from('organization_members')
        .update({ role_id: newRoleId })
        .eq('user_id', memberId)
        .eq('org_id', selectedCompany?.company_id);
    
      if (error) {
        console.error('Error updating role:', error);
        setErrorMsg('Failed to update role. Please try again.');
        setTimeout(() => setErrorMsg(null), 3000);
        return;
      }
    
      setTeamMembers(prev => prev.map(member => 
        member.id === memberId ? { ...member, role_id: newRoleId } : member
      ));

      // If the current user changed their own role, refresh their role ID and context
      if (memberId === user?.id) {
        // Update local state immediately
        setCurrentUserRoleId(newRoleId);
        
        await refresh();
      }

      setShowRoleDropdown(null);
      setSuccessMsg("Role updated successfully")
      setTimeout(() => {
        setSuccessMsg(null);
      }, 2000);
    } catch (error) {
      console.error('Error updating role:', error);
      setErrorMsg('Failed to update role. Please try again.');
      setTimeout(() => setErrorMsg(null), 3000);
    }
  };

  const handleGroupsEditingChange = (editing: boolean, adding: boolean, groupId?: string | null) => {
    setIsEditingGroup(editing);
    setIsAddingGroup(adding);
    setEditingGroupId(groupId || null); 
  };
  
  const handleGroupsSave = async (groupId?: string) => {
    if (!selectedCompany || !editingGroupName.trim()) {
      return;
    }
  
    try {
      if (groupId) {
        const { error } = await supabase
          .from('organization_groups')
          .update({
            name: editingGroupName.trim(),
            description: editingGroupDescription.trim() || null
          })
          .eq('id', groupId)
          .eq('org_id', selectedCompany.company_id);
  
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('organization_groups')
          .insert({
            org_id: selectedCompany.company_id,
            name: editingGroupName.trim(),
            description: editingGroupDescription.trim() || null
          });
  
        if (error) throw error;
      }
  
      // Reset editing state
      setIsEditingGroup(false);
      setIsAddingGroup(false);
      setEditingGroupId(null);
      setEditingGroupName('');
      setEditingGroupDescription('');
      
      // ✅ Call the Groups component's refresh function using ref
      if (refreshGroupsRef.current && typeof refreshGroupsRef.current === 'function') {
        await refreshGroupsRef.current();
      }
    } catch (error: any) {
      console.error('Error saving group:', error);
      alert(error.message || 'Failed to save group');
    }
  };

  const handleGroupsCancel = () => {
    setIsEditingGroup(false);
    setIsAddingGroup(false);
    setEditingGroupId(null);
    setEditingGroupName('');
    setEditingGroupDescription('');
  };

  const fetchTeamMembers = useCallback(async () => {
    if (!selectedCompany) {
      console.log('No company selected');
      setTeamMembers([]);
      setLoading(false);
      return;
    }
  
    try {
      setLoading(true);
      
      // ✅ Fetch pending invitations for this org
      const { data: pendingInvitations, error: invitationError } = await supabase
        .from('user_invitations')
        .select('id, email, role_id, status, org_id')
        .eq('org_id', selectedCompany.company_id)
        .in('status', ['pending', 'sent']); // ✅ Include both pending and sent statuses
      
      // Get organization members (active users)
      const { data: members, error } = await supabase
        .from('organization_members')
        .select(`
          user_id, 
          role_id, 
          is_active
        `)
        .eq('org_id', selectedCompany.company_id)
        .eq('is_active', true);
  
      if (error) {
        console.error('Error fetching team members:', error);
        return;
      }
  
      const userIds = members?.map(m => m.user_id) || [];
      
      // ✅ Create invited members list (users with pending invitations but not yet in org_members)
      const invitedMembers: TeamMember[] = (pendingInvitations || [])
        .filter(inv => !userIds.includes(inv.email)) // ✅ Check if email is not in active members
        .map(inv => ({
          id: inv.email, // ✅ Use email as ID for invited users
          name: inv.email.split('@')[0], // ✅ Use email prefix as name
          email: inv.email,
          role_id: inv.role_id || 5,
          isCurrentUser: false,
          invitationStatus: 'pending' as const,
          invitationId: inv.id,
          phone: 'Not set',
          group_ids: [],
          group_names: []
        }));
  
      
      if (userIds.length > 0) {
        const { data: userDetails, error: userError } = await supabase
          .rpc('get_user_details', { user_ids: userIds });

        if (userError) {
          console.error('Error fetching user details:', userError);
          const transformedMembers: TeamMember[] = members?.map(member => ({
            id: member.user_id,
            name: `User ${member.user_id.slice(0, 8)}`,
            email: 'Email not available',
            role_id: member.role_id,
            isCurrentUser: member.user_id === user?.id,
            group_ids: [],
            group_names: []
          })) || [];
          setTeamMembers(transformedMembers);
          return;
        }

        // Get group memberships for all users from group_members table
        const { data: groupMemberships, error: groupError } = await supabase
          .from('group_members')
          .select(`
            user_id,
            group_id,
            organization_groups(name)
          `)
          .eq('org_id', selectedCompany.company_id)
          .in('user_id', userIds);

        // Create a map of user_id -> groups
        const userGroupsMap: { [key: string]: { id: string, name: string }[] } = {};
        groupMemberships?.forEach((gm: any) => {
          if (!userGroupsMap[gm.user_id]) {
            userGroupsMap[gm.user_id] = [];
          }
          if (gm.organization_groups) {
            userGroupsMap[gm.user_id].push({
              id: gm.group_id,
              name: gm.organization_groups.name
            });
          }
        });

        const transformedMembers: TeamMember[] = members?.map(member => {
          const userDetail = userDetails?.find((u: any) => u.id === member.user_id);
          const memberGroups = userGroupsMap[member.user_id] || [];
          
          return {
            id: member.user_id,
            name: userDetail?.display_name || 'Unknown User',
            email: userDetail?.email || 'No email available',
            role_id: member.role_id,
            isCurrentUser: member.user_id === user?.id,
            phone: userDetail?.contact_number || 'No phone number available',
            group_ids: memberGroups.map(g => g.id),
            group_names: memberGroups.map(g => g.name)
          };
        }) || [];
        
        // ✅ Combine active members and invited members
        const allMembers = [...transformedMembers, ...invitedMembers];
        
        const sortedMembers = allMembers.sort((a, b) => {
          // ✅ Sort: active members first, then invited
          if (a.invitationStatus === 'pending' && b.invitationStatus !== 'pending') return 1;
          if (a.invitationStatus !== 'pending' && b.invitationStatus === 'pending') return -1;
          if (a.isCurrentUser) return -1;
          if (b.isCurrentUser) return 1;
          return a.role_id - b.role_id;
        });
        
        setTeamMembers(sortedMembers);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany?.company_id, user?.id]);

  const handleResendInvitation = async (invitationId: string, email: string, roleId: number) => {
    try {
      setLoading(true);
      setRemovingMember(invitationId);
      
      const { data, error } = await supabase.functions.invoke('resend-invitation', {
        body: {
          invitation_id: invitationId,
          email: email,
          role_id: roleId,
          org_id: selectedCompany?.company_id,
          org_name: selectedCompany?.company_name || 'the organization',
          current_user_id: user?.id
        }
      });
  
      if (error || !data?.success) {
        throw new Error(data?.error || 'Failed to resend invitation');
      }
  
      setSuccessMsg(`Invitation resent to ${email}`);
      setTimeout(() => {
        setSuccessMsg(null);
        fetchTeamMembers();
      }, 2000);
  
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      setErrorMsg(error.message || 'Failed to resend invitation');
      setTimeout(() => setErrorMsg(null), 3000);
    } finally {
      setLoading(false);
      setRemovingMember(null);
    }
  };
  
  const handleCancelInvitation = async (invitationId: string, email: string) => {
    try {
      setLoading(true);
      setRemovingMember(invitationId);
      
      const { data, error } = await supabase.functions.invoke('cancel-invitation', {
        body: {
          invitation_id: invitationId,
          email: email
        }
      });
  
      if (error || !data?.success) {
        throw new Error(data?.error || 'Failed to cancel invitation');
      }
  
      setSuccessMsg(`Invitation cancelled for ${email}`);
      setTimeout(() => {
        setSuccessMsg(null);
        fetchTeamMembers();
      }, 2000);
  
    } catch (error: any) {
      console.error('Error cancelling invitation:', error);
      setErrorMsg(error.message || 'Failed to cancel invitation');
      setTimeout(() => setErrorMsg(null), 3000);
    } finally {
      setLoading(false);
      setRemovingMember(null);
    }
  };

  const fetchGroups = useCallback(async () => {
    if (!selectedCompany) {
      setGroups([]);
      setGroupsLoading(false);
      return;
    }
    
    try {
      setGroupsLoading(true);
      
      const { data, error } = await supabase
        .from('organization_groups')
        .select('id, name, description')
        .eq('org_id', selectedCompany.company_id)
        .order('name');
      
      if (error) {
        console.error('Error fetching groups:', error);
        setGroupsLoading(false);
        return;
      }
      
      // For admins, show all groups. For non-admins, only show groups they belong to
      let filteredGroups = data || [];
      if (!isUserAdmin && currentUserGroupIds.length > 0) {
        filteredGroups = filteredGroups.filter(g => currentUserGroupIds.includes(g.id));
      } else if (!isUserAdmin) {
        // If non-admin has no groups, they can only see "Unassigned"
        setGroups([]);
        setGroupsLoading(false);
        return;
      }
      
      // Count members per group from group_members table
      const groupsWithCounts = await Promise.all(
        filteredGroups.map(async (group) => {
          const { count } = await supabase
            .from('group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id)
            .eq('org_id', selectedCompany.company_id);
          
          return {
            ...group,
            member_count: count || 0
          };
        })
      );
      
      setGroups(groupsWithCounts);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  }, [selectedCompany?.company_id, currentUserGroupIds, isUserAdmin]);

  useEffect(() => {
    const getCurrentUserRole = async () => {
      if (!user || !selectedCompany) return;
      
      const { data: membership } = await supabase
        .from('organization_members')
        .select('role_id')
        .eq('org_id', selectedCompany.company_id)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();
      
      setCurrentUserRoleId(membership?.role_id || 0);
    };
    
    const timeoutId = setTimeout(getCurrentUserRole, 150);
    return () => clearTimeout(timeoutId);
  }, [user, selectedCompany?.company_id]);

  useEffect(() => {
    if (companyId && selectedCompany?.company_id !== companyId) {
      const company = userCompanies.find(c => c.company_id === companyId);
      if (company) {
        setSelectedCompany(company);
      }
    }
  }, [companyId, selectedCompany?.company_id, userCompanies, setSelectedCompany]);

  useEffect(() => {
    const fetchCurrentUserGroups = async () => {
      if (!selectedCompany || !user?.id) {
        setCurrentUserGroupIds([]);
        return;
      }
  
      try {
        const { data, error } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id)
          .eq('org_id', selectedCompany.company_id);
  
        if (error) {
          console.error('Error fetching current user groups:', error);
          return;
        }
  
        const groupIds = data?.map(gm => gm.group_id) || [];
        setCurrentUserGroupIds(groupIds);
      } catch (error) {
        console.error('Error fetching current user groups:', error);
      }
    };
  
    fetchCurrentUserGroups();
  }, [selectedCompany?.company_id, user?.id]);

  useEffect(() => {
    if (selectedCompany && user?.id) {
      const timeoutId = setTimeout(fetchTeamMembers, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedCompany?.company_id, user?.id, fetchTeamMembers]);
    
  useEffect(() => {
    fetchRoleNames();
  }, [fetchRoleNames]);

  useEffect(() => {
    if (selectedCompany) {
      fetchGroups();
    }
  }, [selectedCompany?.company_id, currentUserGroupIds, isUserAdmin, fetchGroups]);

  const filteredMembers = useMemo(() => {
    let members = teamMembers.filter(member =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  
    if (selectedGroupId !== null) {
      members = members.filter(member => 
        member.group_ids?.includes(selectedGroupId)
      );
    }
  
    return members;
  }, [teamMembers, searchTerm, selectedGroupId]);

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    try {
      setRemovingMember(memberId);
      
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('user_id', memberId)
        .eq('org_id', selectedCompany?.company_id);
  
      if (error) {
        console.error('Error removing member:', error);
        setErrorMsg('Failed to remove member. Please try again.');
        return;
      }
  
      setTeamMembers(prev => prev.filter(member => member.id !== memberId));
      setShowRemoveConfirm(null);
      setSuccessMsg(`${memberName} has been removed from the team.`);
      
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (error) {
      console.error('Error removing member:', error);
      setErrorMsg('Failed to remove member. Please try again.');
    } finally {
      setRemovingMember(null);
    }
  };

  const handleLeaveTeam = async () => {
    try {
      setRemovingMember(user?.id || '');
      
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('user_id', user?.id)
        .eq('org_id', selectedCompany?.company_id);
  
      if (error) {
        console.error('Error leaving team:', error);
        setErrorMsg('Failed to leave team. Please try again.');
        return;
      }
  
      window.location.href = '/signin';
    } catch (error) {
      console.error('Error leaving team:', error);
      setErrorMsg('Failed to leave team. Please try again.');
    } finally {
      setRemovingMember(null);
    }
  };

  const handleSaveGroupAssignments = async (memberId: string) => {
    try {
      const member = teamMembers.find(m => m.id === memberId);
      if (!member) return;

      const currentGroups = member.group_ids || [];
      const newGroups = tempSelectedGroups;
      
      // Find groups to add and remove
      const groupsToAdd = newGroups.filter(g => !currentGroups.includes(g));
      const groupsToRemove = currentGroups.filter(g => !newGroups.includes(g));

      // Remove from groups
      if (groupsToRemove.length > 0) {
        const { error } = await supabase
          .from('group_members')
          .delete()
          .eq('user_id', memberId)
          .eq('org_id', selectedCompany?.company_id)
          .in('group_id', groupsToRemove);
        
        if (error) throw error;
      }

      // Add to groups
      if (groupsToAdd.length > 0) {
        const inserts = groupsToAdd.map(groupId => ({
          user_id: memberId,
          group_id: groupId,
          org_id: selectedCompany?.company_id,
          added_by: user?.id
        }));

        const { error } = await supabase
          .from('group_members')
          .insert(inserts);
        
        if (error) throw error;
      }
      
      // Refresh data
      fetchTeamMembers();
      fetchGroups();
      
      setShowGroupAssignmentModal(null);
      setTempSelectedGroups([]);
      setSuccessMsg("Member group assignments updated successfully");
      setTimeout(() => {
        setSuccessMsg(null);
      }, 2000);
    } catch (error) {
      console.error('Error saving group assignments:', error);
      setErrorMsg('Failed to update group assignments');
    }
  };
  
  const getGroupMemberCount = (groupId: string) => {
    return teamMembers.filter(m => m.group_ids?.includes(groupId)).length;
  };        
  
  return (
    <MainLayout>
      <div className="page-container">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">
              People in {selectedCompany?.company_name}
            </h1>
          </div>
          <div className="page-header-right">
            {permissions['team_groups'] && (
              <>
                <button
                  onClick={() => {
                    setIsGroupModalOpen(true); // ✅ Open groups modal instead of member modal
                  }}
                  className="f-btn f-btn-success"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>Manage Groups</span>
                </button>
                </>)}
                {permissions['team_invite'] && (
                 <>
                 <button
                  onClick={() => {
                    setEditingMember(null);
                    setIsModalOpen(true);
                  }}
                  className="f-btn f-btn-primary"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Invite</span>
                </button>
              </>
            )} 
          </div>
        </div>

        {/* Group Filter Dropdown */}
        <div className="page-card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <select
                  value={selectedGroupId || ''}
                  onChange={(e) => setSelectedGroupId(e.target.value === '' ? null : e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                >
                  <option value="">All Members (Default)</option>
                  {groupsLoading ? (
                    <option disabled>Loading groups...</option>
                  ) : (
                    groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({getGroupMemberCount(group.id)})
                      </option>
                    ))
                  )}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Team Members List */}
          <div className="bg-white rounded-lg border border-gray-200">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center px-4 py-2 text-sm text-gray-500">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading team members...
                </div>
              </div>
            ) : filteredMembers.length > 0 ? (
              filteredMembers.map((member, index) => (
                <div key={member.id}>
                  <div className={`flex items-center justify-between p-4 transition-colors ${
                    member.invitationStatus === 'pending' 
                      ? 'bg-blue-50 border-l-4 border-blue-400 hover:bg-blue-100' 
                      : 'hover:bg-gray-50'
                  }`}>
                    {/* Left side - Group Assignment, Avatar and Info */}
                    <div className="flex items-center space-x-4 flex-1">
                      {/* Group Assignment Status - Always visible, but only clickable for admins */}
                      <div className="flex-shrink-0">
                        <button
                          onClick={isUserAdmin ? () => {
                            const memberGroups = member.group_ids || [];
                            setTempSelectedGroups([...memberGroups]);
                            setShowGroupAssignmentModal(member.id);
                          } : undefined}
                          disabled={!isUserAdmin}
                          className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center space-x-1.5 w-[110px] transition-all ${
                            member.group_ids && member.group_ids.length > 0
                              ? isUserAdmin
                                ? 'bg-green-50 border border-green-200 text-green-700 hover:bg-green-100'
                                : 'bg-green-50 border border-green-200 text-green-700 opacity-75 cursor-not-allowed'
                              : isUserAdmin
                                ? 'bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100'
                                : 'bg-gray-50 border border-gray-200 text-gray-600 opacity-75 cursor-not-allowed'
                          }`}
                          title={isUserAdmin ? "Click to assign groups" : "Group assignment status"}
                        >
                          <span className="text-xs font-medium whitespace-nowrap">
                            {member.group_ids && member.group_ids.length > 0 
                              ? 'Assigned'
                              : 'Default'}
                          </span>
                          {isUserAdmin && (
                            <svg 
                              className={`w-3 h-3 transition-transform ${
                                member.group_ids && member.group_ids.length > 0
                                  ? 'text-green-600'
                                  : 'text-gray-500'
                              }`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </button>
                      </div>

                      {/* Avatar and Name */}
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            {member.avatar ? (
                              <img src={member.avatar} alt={member.name} className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                              <span className="text-sm font-medium text-gray-600">
                                {member.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Display Name and Email */}
                        <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {member.name}
                          </p>
                          {member.isCurrentUser && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              You
                            </span>
                          )}
                          {member.invitationStatus === 'pending' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Invited
                            </span>
                          )}
                        </div>
                          <p className="text-xs text-gray-500 italic truncate mt-0.5">
                            {member.email}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Right side - Group Assignment, Role and Actions */}
                    <div className="flex items-center space-x-2">
                      {/* Role Badge */}
                      {member.invitationStatus === 'pending' ? (
                        // Static role badge for invited users - shows intended role
                        <div className={`px-3 py-1.5 rounded-lg text-sm opacity-75 ${
                          member.role_id === 1 ? 'bg-purple-100 text-purple-800' :
                          member.role_id === 2 ? 'bg-blue-100 text-blue-800' :
                          member.role_id === 3 ? 'bg-green-100 text-green-800' :
                          member.role_id === 4 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-orange-100 text-orange-800'
                        }`}
                        title="Role will be assigned upon acceptance">
                          <span className="font-medium">{(roleName[member.role_id] || 'junior').charAt(0).toUpperCase() + (roleName[member.role_id] || 'junior').slice(1)}</span>
                        </div>
                      ) : !isUserAdmin ? (
                        // Non-admin view - static badge
                        <div className={`px-4 py-2 rounded-lg text-sm ${
                          member.role_id === 1 ? 'bg-purple-100 text-purple-800' :
                          member.role_id === 2 ? 'bg-blue-100 text-blue-800' :
                          member.role_id === 3 ? 'bg-green-100 text-green-800' :
                          member.role_id === 4 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          <span className="font-medium">{(roleName[member.role_id] || 'junior').charAt(0).toUpperCase() + (roleName[member.role_id] || 'junior').slice(1)}</span>
                        </div>
                      ) : (
                        <div className="relative role-dropdown-container">
                          {/* Show static badge if current user is owner, otherwise show dropdown */}
                          {member.isCurrentUser && member.role_id === 1 ? (
                            <div className={`px-3 py-1.5 rounded-lg text-sm ${
                              member.role_id === 1 ? 'bg-purple-100 text-purple-800' :
                              member.role_id === 2 ? 'bg-blue-100 text-blue-800' :
                              member.role_id === 3 ? 'bg-green-100 text-green-800' :
                              member.role_id === 4 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              <span className="font-medium">{(roleName[member.role_id] || 'junior').charAt(0).toUpperCase() + (roleName[member.role_id] || 'junior').slice(1)}</span>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  if (member.role_id === 1) {
                                    setErrorMsg('Owner role cannot be changed.');
                                    setTimeout(() => setErrorMsg(null), 3000);
                                    return;
                                  }
                                  if (isUserAdmin && (member.role_id !== 1 || currentUserRoleId === 1)) {
                                    setShowRoleDropdown(showRoleDropdown === member.id ? null : member.id);
                                  }
                                }}
                                disabled={!isUserAdmin || (member.role_id === 1 && currentUserRoleId !== 1) || (member.isCurrentUser && member.role_id === 1)}
                                className={`px-3 py-1.5 rounded-lg text-sm flex items-center space-x-1 transition-colors ${
                                  member.role_id === 1 ? 'bg-purple-100 text-purple-800' :
                                  member.role_id === 2 ? 'bg-blue-100 text-blue-800' :
                                  member.role_id === 3 ? 'bg-green-100 text-green-800' :
                                  member.role_id === 4 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-orange-100 text-orange-800'
                                } ${
                                  !isUserAdmin || (member.role_id === 1 && currentUserRoleId !== 1) || (member.isCurrentUser && member.role_id === 1)
                                    ? 'opacity-50 cursor-not-allowed' 
                                    : 'hover:bg-opacity-80'
                                }`}
                                title={member.role_id === 1 ? 'Owner role cannot be changed' : ''}
                              >
                                <span className="font-medium">{(roleName[member.role_id] || 'junior').charAt(0).toUpperCase() + (roleName[member.role_id] || 'junior').slice(1)}</span>
                                {!(!isUserAdmin || (member.role_id === 1 && currentUserRoleId !== 1) || (member.isCurrentUser && member.role_id === 1)) && (
                                  <svg 
                                    className={`w-4 h-4 ml-2 transition-transform ${
                                      showRoleDropdown === member.id ? 'rotate-180' : ''
                                    }`} 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                  </svg>
                                )}
                              </button>
                              
                              {showRoleDropdown === member.id && 
                                isUserAdmin && (member.role_id !== 1 || currentUserRoleId === 1) && !(member.isCurrentUser && member.role_id === 1) && (
                                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-32">
                                    {getAvailableRoles(currentUserRoleId)
                                      .filter((roleId) => {
                                        // Remove owner role entirely - it should never appear in dropdown
                                        return roleId !== 1;
                                      })
                                      .map((roleId) => {
                                        return (
                                          <button
                                            key={roleId}
                                            onClick={() => handleRoleChange(member.id, roleId)}
                                            className={`w-full text-left px-3 py-2 text-sm first:rounded-t-lg last:rounded-b-lg transition-colors ${
                                              member.role_id === roleId 
                                                ? 'bg-blue-50 text-blue-700 font-medium' 
                                                : 'text-gray-700 hover:bg-gray-100'
                                            }`}
                                          >
                                            <div className="flex items-center space-x-2">
                                              <div className={`w-2 h-2 rounded-full ${
                                                roleId === 1 ? 'bg-purple-500' :
                                                roleId === 2 ? 'bg-blue-500' :
                                                roleId === 3 ? 'bg-green-500' :
                                                roleId === 4 ? 'bg-yellow-500' :
                                                'bg-orange-500'
                                              }`}></div>
                                              <span>{(roleName[roleId] || 'junior').charAt(0).toUpperCase() + (roleName[roleId] || 'junior').slice(1)}</span>
                                            </div>
                                          </button>
                                        );
                                      })}
                                  </div>
                                )}
                            </>
                          )}
                        </div>
                      )}

                      {/* Edit Button - Only show for accepted members, not invited */}
                      {member.invitationStatus !== 'pending' && (isUserAdmin || member.isCurrentUser) ? (
                        <button 
                          onClick={() => {
                            setEditingMember(member);
                            setIsModalOpen(true);
                          }}
                          className={`f-btn f-btn-soft ${
                            member.role_id === 1 && !member.isCurrentUser
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-opacity-80'
                          }`}
                          disabled={
                            (member.role_id === 1 && !member.isCurrentUser) ||
                            (!isUserAdmin && !member.isCurrentUser)
                          }
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Edit</span>
                        </button>
                      ) : null}


                      {/* Leave/Remove Button */}
                      {isUserAdmin && (
                        <>
                          {/* For invited members (pending invitations) */}
                          {member.invitationStatus === 'pending' && member.invitationId ? (
                            <div className="relative invitation-menu-container">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowInvitationMenu(showInvitationMenu === member.id ? null : member.id);
                                }}
                                disabled={loading || removingMember === member.id}
                                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Invitation options"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                </svg>
                              </button>

                              {/* Dropdown Menu */}
                              {showInvitationMenu === member.id && (
                                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px]">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowInvitationMenu(null);
                                      setShowResendInviteConfirm({
                                        id: member.invitationId!,
                                        email: member.email,
                                        roleId: member.role_id
                                      });
                                    }}
                                    disabled={loading || removingMember === member.id}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                  >
                                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    <span>{loading && removingMember === member.id ? 'Resending...' : 'Resend Invitation'}</span>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowInvitationMenu(null);
                                      setShowCancelInviteConfirm({
                                        id: member.invitationId!,
                                        email: member.email
                                      });
                                    }}
                                    disabled={loading || removingMember === member.id}
                                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 last:rounded-b-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                  >
                                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    <span>{loading && removingMember === member.id ? 'Cancelling...' : 'Cancel Invitation'}</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            /* For active members - Keep Leave/Remove Button */
                            member.role_id === 1 ? null : (<button 
                              className={`px-3 py-2 rounded-lg text-sm w-20 text-center f-btn f-btn-soft ${
                                isUserAdmin || member.isCurrentUser
                                  ? 'hover:bg-opacity-80' 
                                  : 'opacity-50 cursor-not-allowed'
                              }`}
                              disabled={
                                (!isUserAdmin && !member.isCurrentUser) ||
                                removingMember === member.id
                              }
                              onClick={() => {
                                if (member.isCurrentUser) {
                                  setShowLeaveConfirm(true);
                                } else {
                                  setShowRemoveConfirm(member.id);
                                }
                              }}
                            >
                              {removingMember === member.id ? 'Removing...' : (member.isCurrentUser ? 'Leave' : 'Remove')}
                            </button>)
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {index < filteredMembers.length - 1 && (
                    <div className="border-t border-gray-200"></div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No members found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {selectedGroupId ? 'This group has no members yet.' : 'Try adjusting your search terms.'}
                </p>
              </div>
            )}
          </div>   
        </div>
        
        {/* Success and Error Messages */}
        {successMsg && (
          <div className="fixed top-1 right-4 z-[10000] bg-blue-50 border border-blue-200 px-6 py-3 rounded-lg shadow-sm flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-blue-800">{successMsg}</span>
            <button 
              onClick={() => setSuccessMsg(null)}
              className="ml-3 text-blue-400 hover:text-blue-600"
            >
              ×
            </button>
          </div>
        )}

        {errorMsg && (
          <div className="fixed top-4 right-4 z-[10000] bg-red-50 border border-red-200 px-6 py-3 rounded-lg shadow-sm flex items-center">
            <svg className="w-5 h-5 mr-2 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800">{errorMsg}</span>
            <button 
              onClick={() => setErrorMsg(null)}
              className="ml-3 text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        )}
      </div>
  
      <TeamMemberModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingMember(null);
          fetchTeamMembers();
        }}
        onSave={(member) => {
          if (editingMember) {
            setTeamMembers(prev => prev.map(m => m.id === member.id ? member : m));
          } else {
            setTeamMembers(prev => [...prev, member]);
          }
        }}
        editingMember={editingMember}
        selectedCompany={selectedCompany}
        currentUserRoleId={currentUserRoleId}
        teamMembers={teamMembers}
      />
  
      {/* Remove Confirmation Modal */}
      {showRemoveConfirm && (() => {
        const memberToRemove = teamMembers.find(m => m.id === showRemoveConfirm);
        return memberToRemove ? (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Remove Team Member</h3>
              
              {/* Member Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                <div className="flex items-center space-x-3 mb-3">
                  {memberToRemove.avatar ? (
                    <img 
                      src={memberToRemove.avatar} 
                      alt={memberToRemove.name}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {memberToRemove.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{memberToRemove.name}</p>
                    <p className="text-sm text-gray-600">{memberToRemove.email}</p>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to remove <strong>{memberToRemove.name}</strong> from the team? They will lose access to all organization data.
              </p>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowRemoveConfirm(null)}
                  className="f-btn f-btn-soft"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleRemoveMember(memberToRemove.id, memberToRemove.name);
                  }}
                  className="f-btn f-btn-danger"
                  disabled={removingMember === memberToRemove.id}
                >
                  {removingMember === memberToRemove.id ? 'Removing...' : 'Remove Member'}
                </button>
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {/* Leave Confirmation Modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Leave Team</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to leave this team? You will lose access to all organization data and will be redirected to the sign-in page.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLeaveConfirm(false);
                  handleLeaveTeam();
                }}
                className="f-btn f-btn-danger"
                disabled={removingMember === user?.id}
              >
                {removingMember === user?.id ? 'Leaving...' : 'Leave Team'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Groups Management Modal */}
      {isGroupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-[800px] h-[600px] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Manage Groups</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Organize team members into groups</p>
                </div>
              </div>
              {/* Close button only in header */}
              <button
                onClick={() => {
                  setIsGroupModalOpen(false);
                  handleGroupsCancel(); // Reset editing state when closing
                  fetchGroups();
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white transition-colors"
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Groups Component Content - Scrollable */}
            <div className="overflow-y-auto flex-1 p-6">
              <Groups
                onEditingChange={handleGroupsEditingChange}
                onSave={handleGroupsSave}
                onCancel={handleGroupsCancel}
                editingGroupId={editingGroupId}
                isAddingGroup={isAddingGroup}
                editingGroupName={editingGroupName}
                editingGroupDescription={editingGroupDescription}
                onEditingGroupNameChange={setEditingGroupName}
                onEditingGroupDescriptionChange={setEditingGroupDescription}
                onRefreshRequest={(refreshFn) => {
                  refreshGroupsRef.current = refreshFn; 
                }}
              />
            </div>

            {/* Modal Footer - Action Buttons */}
            {(isEditingGroup || isAddingGroup) && (
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <button
                  onClick={handleGroupsCancel}
                  className="f-btn f-btn-soft"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleGroupsSave(editingGroupId || undefined)}
                  className="f-btn f-btn-primary"
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Group Assignment Modal */}
      {showGroupAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Assign Groups
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">Select groups for this member</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowGroupAssignmentModal(null);
                  setTempSelectedGroups([]);
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="space-y-1.5 max-h-80 overflow-y-auto pr-2">
                {groups.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-900 mb-1">No groups available</p>
                    <p className="text-xs text-gray-500">Create a group first to assign members</p>
                  </div>
                ) : (
                  groups.map((group) => {
                    const isSelected = tempSelectedGroups.includes(group.id);
                    return (
                      <label
                        key={group.id}
                        className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-blue-50 border-2 border-blue-200 shadow-sm'
                            : 'bg-white border-2 border-gray-100 hover:border-blue-200 hover:bg-blue-50/50'
                        }`}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`relative w-5 h-5 border-2 rounded flex items-center justify-center transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-white'
                              : 'border-gray-300 bg-white'
                          }`}>
                            {isSelected && (
                              <svg 
                                className="w-3.5 h-3.5 text-blue-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="3"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className={`text-sm font-semibold ${
                              isSelected ? 'text-blue-900' : 'text-gray-900'
                            }`}>
                              {group.name}
                            </span>
                          </div>
                          {group.description && (
                            <p className={`text-xs mt-1 ${
                              isSelected ? 'text-blue-700' : 'text-gray-500'
                            }`}>
                              {group.description}
                            </p>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTempSelectedGroups([...tempSelectedGroups, group.id]);
                            } else {
                              setTempSelectedGroups(tempSelectedGroups.filter(id => id !== group.id));
                            }
                          }}
                          className="sr-only"
                        />
                      </label>
                    );
                  })
                )}
              </div>
              
              <div className="flex items-center justify-end space-x-3 pt-4 mt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowGroupAssignmentModal(null);
                    setTempSelectedGroups([]);
                  }}
                  className="px-4 py-2 rounded-lg flex items-center space-x-1 border border-gray-500 text-gray-500 hover:bg-gray-200 transition text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveGroupAssignments(showGroupAssignmentModal)}
                  className="px-4 py-2 rounded-lg flex items-center space-x-1 border border-blue-500 text-blue-500 hover:bg-blue-50 transition text-sm"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resend Invitation Confirmation Modal */}
      {showResendInviteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resend Invitation</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to resend the invitation to {showResendInviteConfirm.email}? This sends a new email and does not delete their account.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowResendInviteConfirm(null)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleResendInvitation(showResendInviteConfirm.id, showResendInviteConfirm.email, showResendInviteConfirm.roleId);
                  setShowResendInviteConfirm(null);
                }}
                className="f-btn f-btn-success"
                disabled={loading || removingMember === showResendInviteConfirm.id}
              >
                {loading && removingMember === showResendInviteConfirm.id ? 'Resending...' : 'Resend Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Invitation Confirmation Modal */}
      {showCancelInviteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Invitation</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel the invitation for {showCancelInviteConfirm.email}? This removes the pending invite only. It does not delete their account.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowCancelInviteConfirm(null)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleCancelInvitation(showCancelInviteConfirm.id, showCancelInviteConfirm.email);
                  setShowCancelInviteConfirm(null);
                }}
                className="f-btn f-btn-danger"
                disabled={loading || removingMember === showCancelInviteConfirm.id}
              >
                {loading && removingMember === showCancelInviteConfirm.id ? 'Cancelling...' : 'Cancel Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      </div>
    </MainLayout>
  );
}